import React from 'react';
import { motion } from 'framer-motion';
import {
  Dna, Clock, Pill, AlertTriangle, Rocket, Droplets, Brain, Activity,
  Utensils, ShoppingCart, ChefHat, CalendarRange, Repeat, Lightbulb, Sparkles, Crown
} from 'lucide-react';
import { renderMarkdown, parseSections } from './markdown';
import { PREMIUM_BG, FOOD_IMAGES } from './media';

// Associe une icône à une section selon des mots-clés du titre
const iconForTitle = (title = '', type) => {
  const t = title.toLowerCase();
  if (/morphotype|profil/.test(t)) return Dna;
  if (/[âa]ge|hormone/.test(t)) return Activity;
  if (/timing|repas|moment/.test(t)) return Clock;
  if (/suppl[ée]ment/.test(t)) return Pill;
  if (/erreur|[ée]viter|mythe|pi[èe]ge/.test(t)) return AlertTriangle;
  if (/strat[ée]gie|avanc/.test(t)) return Rocket;
  if (/hydrat|eau|[ée]lectrolyte/.test(t)) return Droplets;
  if (/mindset|motiv|mental|psycho/.test(t)) return Brain;
  if (/vue d.?ensemble|structure|aper[çc]u/.test(t)) return CalendarRange;
  if (/menu|semaine|jour/.test(t)) return Utensils;
  if (/alternative|substitu|remplac/.test(t)) return Repeat;
  if (/recette/.test(t)) return ChefHat;
  if (/course|liste|achat|shopping/.test(t)) return ShoppingCart;
  if (/conseil|pratique|astuce/.test(t)) return Lightbulb;
  return type === 'meal_plan' ? Utensils : Sparkles;
};

const cardVariants = {
  hidden: { opacity: 0, y: 28 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: Math.min(i * 0.07, 0.6) } }),
};

const PaidContent = ({ content, type, language }) => {
  const allSections = parseSections(content);
  // Retire la carte "titre de document" redondante avec le hero (titre long + corps vide)
  const docTitleRe = /(rapport|plan alimentaire|programme|bilan).*(ultra|complet|d[ée]taill|personnalis)/i;
  const sections = allSections.filter((s) => !(docTitleRe.test(s.title) && !/[a-zA-Z0-9À-ÿ]/.test(s.body)));
  const isMeal = type === 'meal_plan';
  const heroImg = isMeal ? FOOD_IMAGES.bowl : PREMIUM_BG.hero;
  const en = language === 'en';

  const overline = isMeal
    ? (en ? 'CUSTOM MEAL PLAN' : 'PLAN ALIMENTAIRE SUR-MESURE')
    : (en ? 'PRO ANALYSIS' : 'ANALYSE PRO');
  const heroTitle = isMeal
    ? (en ? 'Your custom nutrition program' : 'Votre programme nutrition sur-mesure')
    : (en ? 'Your expert performance report' : 'Votre rapport expert performance');

  return (
    <div className="calc-premium" data-testid={`paid-content-${type}`}>
      {/* Hero banner */}
      <div className="relative overflow-hidden rounded-3xl mb-8 border border-white/10">
        <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
        <div className="relative p-8 sm:p-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-primary/30 mb-4">
            {isMeal ? <Utensils className="w-3.5 h-3.5 text-yellow-400" /> : <Crown className="w-3.5 h-3.5 text-yellow-400" />}
            <span className="calc-overline">{overline}</span>
          </div>
          <h2 className="calc-display text-3xl sm:text-5xl font-black text-white max-w-2xl leading-[1.05]">
            {heroTitle.split(' ').slice(0, -2).join(' ')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-primary">
              {heroTitle.split(' ').slice(-2).join(' ')}
            </span>
          </h2>
        </div>
      </div>

      {/* Section cards */}
      <div className={isMeal ? 'grid grid-cols-1 lg:grid-cols-2 gap-6' : 'grid grid-cols-1 gap-6'}>
        {sections.map((sec, i) => {
          const Icon = iconForTitle(sec.title, type);
          const wide = !isMeal || /menu|semaine|jour|recette|recettes|course|liste|vue d|aper[çc]u/i.test(sec.title);
          return (
            <motion.section
              key={i}
              custom={i}
              variants={cardVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: '-60px' }}
              className={`calc-card group ${wide && isMeal ? 'lg:col-span-2' : ''}`}
            >
              <span className="calc-card-bar" />
              <div className="flex items-center gap-3 mb-4">
                <span className="calc-icon-chip">
                  <Icon className="w-5 h-5" />
                </span>
                {sec.title && <h3 className="calc-display text-lg sm:text-xl font-bold text-white">{sec.title}</h3>}
              </div>
              <div
                className="prose-premium"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(sec.body, { mealImages: isMeal }) }}
              />
            </motion.section>
          );
        })}
      </div>
    </div>
  );
};

export default PaidContent;
